/**
 * Frontend mirror of backend/shared/search/exception_search_aliases.py.
 *
 * The alias map ships inline on every list-endpoint response so this
 * file does not hardcode group definitions - it expands a query term
 * using whatever map the backend returned. Keeps the two surfaces
 * (admin catalog + Inspector Hub) in lockstep.
 */

export type SearchAliasMap = Record<string, string[]>

const MIN_PREFIX_LEN = 3

/**
 * Expand a query term into every alias-equivalent.
 *
 * Two passes, matching the backend:
 *   1. Exact match against an alias-group member - returns the full
 *      group (short canonicals like 'po', 'coa', 'eod' hit here at any
 *      length).
 *   2. Prefix match when the query is at least MIN_PREFIX_LEN chars -
 *      unions every group whose members start with the query, plus the
 *      raw query so substring matching against row content still fires.
 *      Lets 'invoic' expand the same way 'invoice' does as the user
 *      types.
 *
 * Empty / whitespace input returns an empty array.
 */
export function expandSearchTerm(
  term: string,
  aliases: SearchAliasMap | undefined,
): string[] {
  const norm = term.trim().toLowerCase()
  if (!norm) return []
  if (aliases && aliases[norm]) return aliases[norm]
  if (aliases && norm.length >= MIN_PREFIX_LEN) {
    const collected = new Set<string>([norm])
    for (const member of Object.keys(aliases)) {
      if (member.startsWith(norm)) {
        for (const expanded of aliases[member]) collected.add(expanded)
      }
    }
    if (collected.size > 1) return Array.from(collected)
  }
  return [norm]
}

/**
 * Substring-match every expanded term against a pre-lowercased haystack.
 * Returns true if ANY expanded term appears, false if none do. Pass an
 * empty terms array to match everything (the caller decided there was
 * no query to apply).
 */
export function haystackIncludesAnyTerm(
  haystack: string,
  terms: string[],
): boolean {
  if (terms.length === 0) return true
  for (const t of terms) {
    if (haystack.includes(t)) return true
  }
  return false
}
